import { useState } from "react";
import { providers, getProvider } from "../providers";
import type { Game, ProviderNotes } from "../types/game";

export default function ApiTestLab() {
  const [providerId, setProviderId] = useState(providers[0].id);
  const [query, setQuery] = useState("hades");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [raw, setRaw] = useState<unknown>(null);
  const [games, setGames] = useState<Game[] | null>(null);

  const provider = getProvider(providerId);

  async function runSearch(e?: React.FormEvent) {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    setRaw(null);
    setGames(null);
    try {
      const result = await provider.search(query.trim());
      setRaw(result.raw);
      setGames(result.games);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <section>
      <h1 style={{ marginTop: 0 }}>API Test Lab</h1>
      <p style={{ color: "#b3b9c5" }}>
        Pick a provider, run a search, and inspect both the raw response and
        the normalized <code>Game</code> shape.
      </p>

      <form className="lab-controls" onSubmit={runSearch}>
        <select
          value={providerId}
          onChange={(e) => setProviderId(e.target.value)}
          aria-label="Provider"
        >
          {providers.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
        <input
          type="search"
          placeholder="Search for a game (e.g. Hades, Stardew, Halo)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button type="submit" disabled={loading}>
          {loading ? "Searching…" : "Search"}
        </button>
      </form>

      <p style={{ color: "#9098a8", marginTop: 0 }}>{provider.blurb}</p>

      {loading && <div className="status-loading">Loading {provider.label}…</div>}
      {error && <div className="status-error">⚠ {error}</div>}

      {(raw || games) && (
        <div className="panels">
          <div className="panel">
            <h3>Raw API response</h3>
            <pre>{JSON.stringify(raw, null, 2)}</pre>
          </div>
          <div className="panel">
            <h3>Normalized games ({games?.length ?? 0})</h3>
            <pre>{JSON.stringify(games, null, 2)}</pre>
          </div>
        </div>
      )}

      <ViabilityNotes label={provider.label} notes={provider.notes} />
    </section>
  );
}

function ViabilityNotes({
  label,
  notes,
}: {
  label: string;
  notes: ProviderNotes;
}) {
  const tagClass =
    notes.viability === "good" ? "ok" : notes.viability === "ok" ? "warn" : "bad";
  return (
    <div className="notes">
      <h3>
        <span className={`tag ${tagClass}`}>{notes.viability}</span>
        {label} — viability
      </h3>
      <p>{notes.summary}</p>
      <ul>
        <li>
          <strong>Requires API key:</strong>{" "}
          {notes.requiresApiKey ? "yes" : "no"}
        </li>
        <li>
          <strong>Callable from the browser:</strong>{" "}
          {notes.callableFromBrowser}
        </li>
        <li>
          <strong>CORS:</strong> {notes.corsStatus}
        </li>
        <li>
          <strong>Needs proxy / Pages Function:</strong>{" "}
          {notes.needsProxy ? "yes" : "no"}
        </li>
        <li>
          <strong>Useful fields:</strong> {notes.provides.join(", ")}
        </li>
        <li>
          <strong>Missing fields:</strong> {notes.missing.join(", ")}
        </li>
      </ul>
    </div>
  );
}
